import { Badge, Dropdown, Menu, Radio, Switch } from 'antd';
import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  TextInput,
  ReloadIcon as reloadIcon,
  ColoredNextIcon as coloredNextIcon,
  ColoredPreviousIcon as coloredPreviousIcon,
  TablesFilterIcon as filterIcon,
  NextIcon as nextIcon,
  PreviousIcon as previousIcon,
  ReCaptureStrip as reCaptureIcon,
} from '@/shared/ui';
import { IS_NOT_EDIT_REPORT_MESSAGE } from '@/shared/config';
import usePaginationContainer from './usePaginationContainer';

const menuOptions = [
  {
    label: 'Show all events',
    value: 'ALL',
  },
  {
    label: 'Only show auto captured events',
    value: 'AUTO',
  },
  {
    label: 'Only show manually captured events',
    value: 'MANUAL',
  },
];

export type PaginationContainerProps = {
  isDisabled?: boolean;
  isNotEdit?: boolean;
};

/**
 * Renders the auto-events toolbar holding the hide-excluded switch, reload, re-capture, view filter and page navigation. Reads paging and filter state from usePaginationContainer, builds radio menu items from menuOptions for the filter dropdown, badges the filter icon whenever showCaptureType is not ALL, and blocks re-capture with an explanatory tooltip when the report is not editable.
 *
 * @param props - The isDisabled and isNotEdit flags gating the paging and re-capture controls.
 * @returns The pagination and filter toolbar row.
 */
const PaginationContainer = ({ isDisabled, isNotEdit }: PaginationContainerProps) => {
  const {
    stripFilter,
    isDisabledPrev,
    isDisabledNext,
    maxPageNumber,
    pageString,
    inputSize,
    isError,
    visible,
    filterValue,
    onClickPrevious,
    onClickNext,
    onClickReCaptureStrip,
    handleVisibleChange,
    onChangePageString,
    onKeyDown,
    onBlur,
    onChangeSwitch,
    onClickReloadButton,
    onClickItem,
    onClickApplyFilter,
  } = usePaginationContainer({ isDisabled });

  const items = _.map(menuOptions, (x: any, i: any) => ({
    key: `bulk-action-${i}`,
    label: (
      <Radio
        key={i}
        checked={x.value === filterValue}
        value={x.value}
        className="bulk-action-radio"
      >
        <div className="bulk-action-item">
          <div className="item-label">{x.label}</div>
        </div>
      </Radio>
    ),
    value: x.value,
  }));

  return (
    <div className="pagination-container">
      <div className="switch-button">
        <span id="hide-excluded-from-report-label" className="switch-buton-text">
          Hide excluded from report
        </span>
        <Switch
          id="hide-excluded-from-report-switch"
          aria-labelledby="hide-excluded-from-report-label"
          checked={stripFilter.isHideExcludeReport}
          onChange={onChangeSwitch}
          size="small"
        />
      </div>
      <IconButton
        id="auto-event-reload-btn"
        isOutline
        ariaLabel="Reload"
        iconComponent={<img src={reloadIcon} alt="Noise icon" />}
        onClick={onClickReloadButton}
        className="reset-btn"
      />
      <IconButton
        isFilled
        ariaLabel={isNotEdit ? IS_NOT_EDIT_REPORT_MESSAGE : 'Re-capture'}
        iconComponent={<img src={reCaptureIcon} alt="" />}
        onClick={onClickReCaptureStrip}
        id="re-capture-strip-icon"
        disabled={isNotEdit}
      />
      <UncontrolledTooltip
        target="re-capture-strip-icon"
        className="custom-tooltip"
        placement="bottom"
        delay={{ show: 0, hide: 0 }}
        hideArrow
      >
        {isNotEdit ? IS_NOT_EDIT_REPORT_MESSAGE : 'Re-capture'}
      </UncontrolledTooltip>
      <Dropdown
        trigger={['click']}
        disabled={isDisabled}
        placement="bottom"
        onVisibleChange={handleVisibleChange}
        visible={visible}
        arrow={{ pointAtCenter: true }}
        overlay={
          <div className="bulk-action-menu-wrapper strip-management-filter-menu">
            <div className="bulk-action-menu-desc">View option</div>
            <Menu onClick={onClickItem} className="bulk-action-menu" items={items} />
            <button
              id="strips-filter-apply-btn"
              className="filter-apply-button"
              onClick={onClickApplyFilter}
            >
              Apply
            </button>
          </div>
        }
      >
        <div>
          <Badge count={stripFilter.showCaptureType === 'ALL' ? 0 : 1} className="badge-filter">
            <IconButton
              id="filterStripEventBtn"
              isFilled
              iconComponent={<img src={filterIcon} alt="filter icon" />}
            />
          </Badge>
          {stripFilter.showCaptureType !== 'ALL' && (
            <UncontrolledTooltip
              target="filterStripEventBtn"
              className="custom-tooltip tooltip-filter-event"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
            >
              {stripFilter.showCaptureType === 'AUTO'
                ? 'Only show auto captured events'
                : 'Only show manually captured events'}
            </UncontrolledTooltip>
          )}
        </div>
      </Dropdown>
      <div className="divider" />
      <IconButton
        id="auto-event-previous-page-btn"
        isFilled
        disabled={isDisabledPrev || isDisabled}
        iconComponent={
          <img src={isDisabledPrev ? previousIcon : coloredPreviousIcon} alt="Previous icon" />
        }
        onClick={onClickPrevious}
      />
      <TextInput
        id="auto-event-page-input"
        ariaLabel="Page number"
        suffix={`/ ${maxPageNumber}`}
        name="pageString"
        value={pageString}
        onChange={onChangePageString}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        isNumberFormat
        shouldChangeSize
        className="page-input"
        size={inputSize}
        isError={isError}
        disabled={isDisabled}
        decimalSeparator={false}
      />
      <IconButton
        id="auto-event-next-page-btn"
        isFilled
        disabled={isDisabledNext || isDisabled}
        iconComponent={<img src={isDisabledNext ? nextIcon : coloredNextIcon} alt="Next icon" />}
        onClick={onClickNext}
      />
    </div>
  );
};

export default PaginationContainer;
